import React, { useEffect, useRef, useState } from "react";
import MembersPanel from "./MembersPanel";

/**
 * AccountMenu — knop in de header van de terminal met lidmaatschap-info,
 * "Abonnement beheren" (Stripe Customer Portal) en de Discord-koppeling.
 */
const C = {
  panel: "#0b1018", border: "#1a2535", dim: "#8aabb8", bright: "#c8dae8",
  accent: "#00d4ff", green: "#00e5a0", yellow: "#ffd060", red: "#ff3f50", discord: "#5865f2",
};

function fmtDate(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function membershipLine(user, m) {
  if (!m) return "";
  if (user && user.role === "coach") return "Coach account";
  if (m.comp) return "Complimentary access";
  if (m.status === "past_due") return "Payment failed — please update your payment details";
  if (m.cancelAtPeriodEnd && m.until) return `Cancelled — access until ${fmtDate(m.until)}`;
  if ((m.status === "active" || m.status === "trialing") && m.until) return `Pro member — renews on ${fmtDate(m.until)}`;
  return "No active membership";
}

const btn = (color) => ({
  display: "block", width: "100%", padding: "8px 10px", marginTop: 8, textAlign: "left",
  background: "transparent", border: `1px solid ${color}55`, color, borderRadius: 2,
  fontFamily: "monospace", fontSize: 11, cursor: "pointer",
});

export default function AccountMenu({ auth }) {
  const [open, setOpen] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const ref = useRef(null);
  const { user, membership: m, discord: d } = auth;

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!user) return null;
  const pastDue = m && m.status === "past_due";

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button className="icon-btn" title="Account & membership" onClick={() => setOpen((o) => !o)}
        style={pastDue ? { color: C.yellow } : undefined}>
        &#128100;
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 1000, width: 280,
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, padding: 16,
          fontFamily: "'Courier New', monospace", boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        }}>
          <div style={{ fontSize: 12, color: C.bright }}>{user.name || user.username}</div>
          <div style={{ fontSize: 10, color: pastDue ? C.yellow : C.dim, marginTop: 4, lineHeight: 1.4 }}>
            {membershipLine(user, m)}
          </div>

          {user.role === "coach" && (
            <button style={btn(C.green)} onClick={() => { setShowMembers(true); setOpen(false); }}>
              Members overview
            </button>
          )}

          {m && m.hasBilling && (
            <button style={btn(C.accent)} onClick={auth.openBillingPortal}>
              Manage subscription (cancel, payment method, invoices)
            </button>
          )}

          {d && d.enabled && (
            d.linked ? (
              <>
                <div style={{ fontSize: 10, color: C.green, marginTop: 12 }}>✓ Discord linked — Pro role active</div>
                <button style={btn(C.dim)} onClick={auth.unlinkDiscord}>Unlink Discord</button>
              </>
            ) : (
              <button style={btn(C.discord)} onClick={auth.linkDiscord}>
                Link Discord to get the Pro member role
              </button>
            )
          )}

          <a href="/pnf/" style={{ ...btn(C.dim), textDecoration: "none", boxSizing: "border-box" }}>Open PnF Terminal</a>

          {auth.error && <div style={{ fontSize: 10, color: C.red, marginTop: 10 }}>{auth.error}</div>}
        </div>
      )}
      {showMembers && <MembersPanel onClose={() => setShowMembers(false)} />}
    </div>
  );
}
